import { useCallback, useEffect, useRef, useState } from 'react';
import { ActionId } from '@/game/InputManager';
import { Game } from '@/game/Game';
import { GameSnapshot } from '@/game/Snapshot';

/**
 * On-screen flight controls.
 *
 * Everything here states a demand through `InputManager`'s virtual-input API
 * and nothing here talks to the simulation directly — a thumbstick sets the
 * same body-axis translation demand the WASD keys set, a hold button holds the
 * same action a key holds, a tap fires the same one-shot. That is what keeps
 * the flight model honest on a phone: touch is another way to ask the flight
 * computer for thrust, not a shortcut that moves the vessel itself. (See
 * README §"What is actually simulated" — nothing may set position or velocity
 * directly, and a touch UI is no exception.)
 *
 * The three control sets below match the three things the player can be: on
 * foot, flying the vessel, or detached in the free camera. They share the
 * stick and differ in the buttons, because the stick means "move me" in all
 * three and the buttons do not.
 */

const DEAD_ZONE = 0.09;

/** Remaps a raw stick offset so small wobbles read as centred, not as drift. */
function applyDeadZone(x: number, y: number): [number, number] {
  const len = Math.hypot(x, y);
  if (len <= DEAD_ZONE) return [0, 0];
  const scaled = Math.min(1, (len - DEAD_ZONE) / (1 - DEAD_ZONE)) / len;
  return [x * scaled, y * scaled];
}

/**
 * Analogue thumbstick.
 *
 * Reports -1..1 with +y away from the player (forward/up on screen), which is
 * the sign the caller wants for a "push forward to go forward" control; the
 * DOM's y axis points the other way and is negated here once, rather than at
 * each of the three call sites.
 */
function Thumbstick({
  label,
  onChange,
}: {
  label: string;
  onChange: (x: number, y: number) => void;
}): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const active = useRef<number | null>(null);
  const [knob, setKnob] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const release = useCallback(() => {
    active.current = null;
    setKnob({ x: 0, y: 0 });
    onChange(0, 0);
  }, [onChange]);

  const track = useCallback(
    (clientX: number, clientY: number) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const radius = Math.max(1, rect.width / 2 - 8);
      let dx = clientX - cx;
      let dy = clientY - cy;
      const len = Math.hypot(dx, dy);
      if (len > radius) {
        dx = (dx / len) * radius;
        dy = (dy / len) * radius;
      }
      setKnob({ x: dx, y: dy });
      const [nx, ny] = applyDeadZone(dx / radius, -dy / radius);
      onChange(nx, ny);
    },
    [onChange],
  );

  // Released on unmount as well as on pointer-up: a stick that disappears
  // because the player boarded the ship mid-push would otherwise leave its
  // last demand latched in the input manager forever.
  useEffect(() => () => onChange(0, 0), [onChange]);

  return (
    <div
      ref={ref}
      className="touch-stick"
      role="application"
      aria-label={label}
      onPointerDown={(e) => {
        if (active.current !== null) return;
        active.current = e.pointerId;
        e.currentTarget.setPointerCapture(e.pointerId);
        track(e.clientX, e.clientY);
      }}
      onPointerMove={(e) => {
        if (active.current !== e.pointerId) return;
        track(e.clientX, e.clientY);
      }}
      onPointerUp={(e) => {
        if (active.current === e.pointerId) release();
      }}
      onPointerCancel={(e) => {
        if (active.current === e.pointerId) release();
      }}
      onLostPointerCapture={(e) => {
        if (active.current === e.pointerId) release();
      }}
    >
      <div className="touch-stick-ring" />
      <div className="touch-stick-knob" style={{ transform: `translate(${knob.x}px, ${knob.y}px)` }} />
      <span className="touch-stick-label">{label}</span>
    </div>
  );
}

/** Button that holds an action for as long as it is touched. */
function HoldButton({
  game,
  action,
  children,
  className,
  title,
}: {
  game: Game;
  action: ActionId;
  children: React.ReactNode;
  className?: string;
  title?: string;
}): JSX.Element {
  const [down, setDown] = useState(false);
  const set = useCallback(
    (state: boolean) => {
      setDown(state);
      game.input.setVirtualAction(action, state);
    },
    [game, action],
  );

  // Same reasoning as the stick: unmounting mid-press must not latch the
  // action down, and an unmount is exactly what switching control sets does.
  useEffect(() => () => game.input.setVirtualAction(action, false), [game, action]);

  return (
    <button
      type="button"
      title={title}
      className={`touch-btn${down ? ' held' : ''}${className ? ` ${className}` : ''}`}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        set(true);
      }}
      onPointerUp={() => set(false)}
      onPointerCancel={() => set(false)}
      onLostPointerCapture={() => set(false)}
      // A held control must never also fire the browser's click/context
      // gestures — a long press on it is normal use, not a request for a
      // selection handle or a callout.
      onContextMenu={(e) => e.preventDefault()}
    >
      {children}
    </button>
  );
}

/** Button that fires an action once per tap. */
function TapButton({
  game,
  action,
  children,
  active,
  title,
}: {
  game: Game;
  action: ActionId;
  children: React.ReactNode;
  active?: boolean;
  title?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      title={title}
      className={`touch-btn${active ? ' active' : ''}`}
      onPointerDown={(e) => {
        e.preventDefault();
        game.input.pressVirtualAction(action);
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {children}
    </button>
  );
}

/** Vertical cruise-throttle slider. */
function ThrottleSlider({ game }: { game: Game }): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const active = useRef<number | null>(null);
  // Mirrors `input.axes.throttle` rather than owning it: the keys, a macro or
  // a gamepad trigger can all move the real value, and a slider that only ever
  // showed its own last drag would quietly lie about the engine setting.
  const [value, setValue] = useState(() => game.input.axes.throttle);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (active.current === null) setValue(game.input.axes.throttle);
    }, 250);
    return () => window.clearInterval(id);
  }, [game]);

  const track = useCallback(
    (clientY: number) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const v = Math.max(0, Math.min(1, 1 - (clientY - rect.top) / Math.max(1, rect.height)));
      setValue(v);
      game.input.setThrottle(v);
    },
    [game],
  );

  return (
    <div
      ref={ref}
      className="touch-throttle"
      role="slider"
      aria-label="Throttle"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      onPointerDown={(e) => {
        active.current = e.pointerId;
        e.currentTarget.setPointerCapture(e.pointerId);
        track(e.clientY);
      }}
      onPointerMove={(e) => {
        if (active.current === e.pointerId) track(e.clientY);
      }}
      onPointerUp={() => {
        active.current = null;
      }}
      onPointerCancel={() => {
        active.current = null;
      }}
      onLostPointerCapture={() => {
        active.current = null;
      }}
    >
      <div className="touch-throttle-fill" style={{ height: `${value * 100}%` }} />
      <span className="touch-throttle-label">{Math.round(value * 100)}%</span>
    </div>
  );
}

export function TouchControls({ game, snapshot }: { game: Game; snapshot: GameSnapshot }): JSX.Element {
  const [tray, setTray] = useState(false);
  const onFoot = snapshot.player.onFoot;
  const free = snapshot.player.freeFlight !== null;
  const v = snapshot.vessel;

  const move = useCallback(
    (x: number, y: number) => {
      // Screen right is a demand to strafe right, which is the *negative* port
      // axis — the same sign flip `Game` applies to the on-foot move vector.
      game.input.setVirtualTranslation(-x, 0, y);
    },
    [game],
  );

  const stickLabel = onFoot ? 'Walk' : free ? 'Fly camera' : 'Translate';

  return (
    <div className="touch-controls">
      <div className="touch-left">
        <Thumbstick label={stickLabel} onChange={move} />
      </div>

      <div className="touch-right">
        {!onFoot && !free && <ThrottleSlider game={game} />}

        <div className="touch-cluster">
          {onFoot ? (
            <>
              <HoldButton game={game} action="sprint" title="Sprint">
                RUN
              </HoldButton>
              <HoldButton game={game} action="jump" title="Jump">
                JUMP
              </HoldButton>
              <TapButton game={game} action="boardShip" title="Board the vessel">
                BOARD
              </TapButton>
            </>
          ) : free ? (
            <>
              <HoldButton game={game} action="translateUp" title="Rise">
                ▲
              </HoldButton>
              <HoldButton game={game} action="translateDown" title="Descend">
                ▼
              </HoldButton>
              <HoldButton game={game} action="boost" title="Boost">
                FAST
              </HoldButton>
              <TapButton game={game} action="toggleFreeFlight" title="Leave free flight">
                EXIT
              </TapButton>
            </>
          ) : (
            <>
              <HoldButton game={game} action="translateUp" title="RCS up">
                ▲
              </HoldButton>
              <HoldButton game={game} action="translateDown" title="RCS down">
                ▼
              </HoldButton>
              <HoldButton game={game} action="rollLeft" title="Roll left">
                ↺
              </HoldButton>
              <HoldButton game={game} action="rollRight" title="Roll right">
                ↻
              </HoldButton>
              <HoldButton game={game} action="boost" title="Full thrust while held">
                BOOST
              </HoldButton>
              <TapButton
                game={game}
                action="toggleRotationDamping"
                active={v.holds.rotationDamping}
                title="Rotation damping"
              >
                SAS
              </TapButton>
            </>
          )}
        </div>
      </div>

      {/* Secondary actions: everything a keyboard reaches with a letter and a
          phone has no room to show at once. Collapsed by default so the
          constantly-used cluster above keeps the corner to itself. */}
      <div className={`touch-tray${tray ? ' open' : ''}`}>
        <button type="button" className="touch-btn touch-tray-toggle" onClick={() => setTray((t) => !t)}>
          {tray ? 'CLOSE' : 'MORE'}
        </button>
        {tray && (
          <div className="touch-tray-body">
            {!onFoot && !free && (
              <>
                <div className="touch-tray-group">
                  <span className="touch-tray-title">Engines</span>
                  <TapButton game={game} action="toggleMainEngine" active={v.engineGroups.main}>
                    MAIN
                  </TapButton>
                  <TapButton game={game} action="toggleAtmosphericEngines" active={v.engineGroups.atmospheric}>
                    ATMO
                  </TapButton>
                  <TapButton game={game} action="toggleLandingThrusters" active={v.engineGroups.landing}>
                    LAND
                  </TapButton>
                  <TapButton game={game} action="throttleMax">
                    MAX
                  </TapButton>
                  <TapButton game={game} action="throttleCut">
                    CUT
                  </TapButton>
                </div>
                <div className="touch-tray-group">
                  <span className="touch-tray-title">Hold</span>
                  <TapButton game={game} action="hover" active={v.holds.hoverAssist}>
                    HOVER
                  </TapButton>
                  <TapButton game={game} action="holdAltitude" active={v.holds.altitudeHold}>
                    ALT
                  </TapButton>
                  <TapButton game={game} action="alignPrograde" active={v.alignment === 'prograde'}>
                    PRO
                  </TapButton>
                  <TapButton game={game} action="alignRetrograde" active={v.alignment === 'retrograde'}>
                    RETRO
                  </TapButton>
                  <TapButton game={game} action="alignSurfaceUp" active={v.alignment === 'surfaceUp'}>
                    UP
                  </TapButton>
                </div>
                <div className="touch-tray-group">
                  <span className="touch-tray-title">Macros</span>
                  <TapButton game={game} action="macroLaunch">
                    LAUNCH
                  </TapButton>
                  <TapButton game={game} action="macroCircularise">
                    CIRC
                  </TapButton>
                  <TapButton game={game} action="macroLand">
                    LAND
                  </TapButton>
                  <TapButton game={game} action="macroTransfer">
                    XFER
                  </TapButton>
                  <TapButton game={game} action="macroAbort">
                    ABORT
                  </TapButton>
                </div>
              </>
            )}
            <div className="touch-tray-group">
              <span className="touch-tray-title">Time</span>
              <TapButton game={game} action="warpDown">
                WARP −
              </TapButton>
              <TapButton game={game} action="warpUp">
                WARP +
              </TapButton>
              <TapButton game={game} action="warpReset">
                1x
              </TapButton>
              <TapButton game={game} action="pause" active={snapshot.paused}>
                PAUSE
              </TapButton>
            </div>
            <div className="touch-tray-group">
              <span className="touch-tray-title">View</span>
              <TapButton game={game} action="toggleView">
                CAM
              </TapButton>
              <TapButton game={game} action="toggleFreeFlight" active={free}>
                FREE
              </TapButton>
              <TapButton game={game} action="quickSave">
                SAVE
              </TapButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
